import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { useEffect, useRef, useState } from 'react'
import { useErrorDisplay } from '../components/ErrorDisplay';
import BusySpinner from "../components/BusySpinner";
import DashboardTalentCard from '../ui-components/DashboardTalentCard';
import { TalentErrors } from '../apiClients/TalentClient';
import { useAccessInfo } from '../contexts/AccessTokenProvider';
import OverviewClient, { DashboardCard } from '../apiClients/OverviewClient';
import PageTitle from '../components/PageTitle';
import { useSearchBox } from '../components/SearchBox';
import PanelSegment from '../components/PanelSegment';
import { I18N } from '../I18N';
import RefreshIcon from '@mui/icons-material/Refresh';
import useViewportMode from '../hooks/useViewportMode';
import OnboardingChecklistCard from '../components/onboarding/OnboardingChecklistCard';

const DashboardPage = () => {

  const [ErrorDisplay, setError] = useErrorDisplay();
  const [busy, setBusy] = useState<boolean>(false);
  const initialized = useRef<boolean>(false);
  const { isLoggedIn } = useAccessInfo();
  const { mode, isDesktop, isTablet } = useViewportMode();
  const [SearchBox, searchFilter] = useSearchBox();
  // const theme = useTheme();

  const [dashboardCards, setDashboardCards] = useState<DashboardCard[]>([]);

  const loadData = async () => {
    try {
      setBusy(true);

      var talentGraphs: DashboardCard[] = await new OverviewClient().GetAssessorTalentOverview();
      talentGraphs = [...talentGraphs.sort((a, b) => sortGraphs(a, b))];
      setDashboardCards(talentGraphs);

    } catch (error) {
      setError(error, TalentErrors.GetAll);
    }
    finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!isLoggedIn() || initialized.current)
      return;

    initialized.current = true;
    loadData();
  });

  const sortGraphs = (a: DashboardCard, b: DashboardCard) => {
    let sa = a.FullName.toLocaleLowerCase();
    let sb = b.FullName.toLocaleLowerCase();

    if (sa < sb) {
      return -1;
    }
    if (sa > sb) {
      return 1;
    }
    return 0;

  };

  return (
    <Box
      data-testid="dashboard-page"
      sx={isDesktop ? {
        flex: '1 1 auto',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      } : undefined}>
      <ErrorDisplay />
      <BusySpinner busy={busy}>
        <PanelSegment
          titleContent={<PageTitle title={I18N.Translate("Dashboard")} />}
          toolbar={
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Refresh">
                <span>
                  <IconButton
                    size="small"
                    onClick={loadData}
                    disabled={busy}
                    sx={!isDesktop ? { width: 44, height: 44 } : undefined}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <SearchBox />
            </Stack>
          }
          fillHeight
          boundedHeight={isDesktop}>
          <Box sx={isDesktop ? { flex: '1 1 auto', minHeight: 0, display: 'flex', overflow: 'hidden' } : undefined}>
            <Box
              data-testid="dashboard-grid"
              data-viewport-mode={mode}
              data-layout={isDesktop ? 'desktop-grid' : isTablet ? 'tablet-square-grid' : 'single-column'}
              sx={isDesktop ? {
                flex: '1 1 auto',
                minHeight: 0,
                overflowY: 'auto',
                overscrollBehavior: 'contain',
                px: 2,
                py: 2,
              } : {
                marginTop: 0,
                height: 'auto',
                minHeight: 0,
                overflow: 'visible',
                px: { xs: 1.5, sm: 2 },
                py: { xs: 1.5, sm: 2 },
              }}>
              <Box data-testid="dashboard-onboarding-slot" sx={{ width: '100%', mb: 3, '&:empty': { display: 'none' } }}>
                <OnboardingChecklistCard />
              </Box>
              <Box
                data-testid="dashboard-talent-grid"
                data-column-count={isDesktop || isTablet ? 'auto-fill' : 1}
                sx={{
                  display: 'grid',
                  gridAutoFlow: 'row',
                  justifyContent: 'start',
                  alignContent: 'start',
                  alignItems: 'start',
                  gap: 3,
                  gridTemplateColumns: isDesktop || isTablet
                    ? 'repeat(auto-fill, minmax(min(100%, 336px), 336px))'
                    : 'minmax(0, 1fr)',
                }}>
                {searchFilter(dashboardCards).map(card => (
                  <Box
                    key={card.TalentId}
                    data-testid="dashboard-card-slot"
                    sx={isDesktop || isTablet ? {
                      minWidth: 0,
                      height: 440,
                    } : { minWidth: 0, height: 'auto' }}>
                    <DashboardTalentCard card={card} onRefresh={loadData} />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </PanelSegment>
      </BusySpinner>
    </Box>
  )
};

export default DashboardPage
